import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '../app/StyleGuide';
import { Image, Container } from './Layout';
import { Input } from './FormElements';
import {
  MediumOutlineButton,
  SmallRoundedIconButton,
  Buttons,
  Button,
  PrimaryButton,
  ImageButtonGroup,
} from './Buttons';
import Overlay from './Overlay';
import uuid from '../services/uuid';
import ImageCropAndPreview from './ImageCropAndPreview';

export const BaseImageCrop = ({ defaultImage, onUpdate, aspectRatio, children, isCircularCrop, autoClose = true }) => {
  const [imageToCrop, setImageToCrop] = useState('');
  const [croppedImage, setCroppedImage] = useState(defaultImage || '');

  const fileInputRef = useRef();
  const onFileSelected = e => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setImageToCrop(reader.result));
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (autoClose) setImageToCrop('');
    onUpdate(croppedImage);
  }, [croppedImage]);

  return (
    <>
      <Input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        onChange={onFileSelected}
        style={{ display: 'none' }}
        files={imageToCrop}
        value=""
        data-qa-hook="poster-file-input"
      />
      {children({
        inputRef: fileInputRef,
        image: croppedImage,
        resetImage: () => setCroppedImage(''),
        closeModal: () => setImageToCrop(''),
      })}
      {imageToCrop && (
        <ImageCropOverlay
          image={imageToCrop}
          onComplete={setCroppedImage}
          onDismiss={() => setImageToCrop('')}
          aspectRatio={aspectRatio}
          isCircularCrop={isCircularCrop}
        />
      )}
    </>
  );
};

export default function ImageCrop({
  defaultImage,
  onUpdate,
  onRemove,
  buttonText = 'Upload Image',
  aspectRatio = 6 / 1,
  showRemoveButton = true,
}) {
  return (
    <BaseImageCrop defaultImage={defaultImage} onUpdate={onUpdate} aspectRatio={aspectRatio} isCircularCrop={false}>
      {({ inputRef, image, resetImage }) =>
        image ? (
          <>
            <Image src={image} style={{ width: '100%' }} />

            {showRemoveButton && (
              <ImageButtonGroup>
                <SmallRoundedIconButton
                  aria-label="Remove Image"
                  data-tracking-id="image-crop-remove"
                  className="transparent"
                  onClick={onRemove || resetImage}
                >
                  <i className="far fa-trash-alt"></i>
                </SmallRoundedIconButton>
              </ImageButtonGroup>
            )}
          </>
        ) : (
          <MediumOutlineButton
            data-tracking-id="image-crop-upload"
            onClick={() => inputRef.current.click()}
            style={{ maxWidth: '147px' }}
          >
            {buttonText}
          </MediumOutlineButton>
        )
      }
    </BaseImageCrop>
  );
}

ImageCrop.propTypes = {
  defaultImage: PropTypes.string,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  buttonText: PropTypes.string,
  aspectRatio: PropTypes.number,
  showRemoveButton: PropTypes.bool,
  isCircularCrop: PropTypes.bool,
};

export const ImageCropUtils = {
  isCroppedImage: image => image && image.indexOf('data:image/') === 0,
  convertCroppedImageToBlob: image => fetch(image).then(res => res.blob()),
  generateFilename: prefix => (prefix || uuid.generate()) + '.png',
};

const ImageCropOverlay = ({ image, onComplete, onDismiss, aspectRatio = 16 / 9, isCircularCrop }) => {
  const [getCanvas, setGetCanvas] = useState(() => {});
  const [isOperating, setIsOperating] = useState(false);

  const onImageApprove = canvas => {
    setIsOperating(true);
    return canvas.toDataURL();
  };

  return (
    <Overlay dismissHandler={onDismiss}>
      <Container style={{ padding: Grid._6 }}>
        <ImageCropAndPreview
          image={image}
          onCropUpdate={setGetCanvas}
          aspectRatio={aspectRatio}
          isCircularCrop={isCircularCrop}
        />
        <Buttons style={{ justifyContent: 'flex-end' }}>
          <Button data-tracking-id="image-crop-cancel" onClick={onDismiss}>
            Cancel
          </Button>
          <PrimaryButton
            data-tracking-id="image-crop-confirm"
            data-qa-hook="imageApprove"
            operating={isOperating}
            onClick={() => onComplete(onImageApprove(getCanvas()))}
          >
            Done
          </PrimaryButton>
        </Buttons>
      </Container>
    </Overlay>
  );
};
ImageCropOverlay.propTypes = {
  image: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  aspectRatio: PropTypes.number,
  isCircularCrop: PropTypes.bool,
};
